// file: src/store/appStore.js

import axios from 'axios';
import {
  observable,
  computed,
  makeObservable,
  action,
  runInAction,
} from 'mobx';
import { configure } from 'mobx';
import FuzzySet from 'fuzzyset';

import config from './config';
import ApiStore from './api/ApiStore';
import axiosAuthAPI from './api/axiosAuthAPI';
import SubscriptionService from './api/SubscriptionService';
import TOOLS from './tools';
import { CarSimple } from '@phosphor-icons/react';

configure({ enforceActions: 'never' });

class AppStore {
  @observable isLoggedIn = false;
  @observable profile = {
    _id: null,
    username: '',
    email: '',
    organization: '',
  };

  @observable redirect = '';
  @observable organizationDetails = null;
  @observable loginLoading = false;
  @observable landingPageUrl = config.landingPageUrl;
  @observable requiresSubscriptionSetup = false;

  // Tools & fuzzy searching
  @observable toolsKeyword = '';
  @observable copyToClipboardText = '';
  @observable feedback = '';

  // Chat Observables
  @observable chatMessages = [];
  @observable chatLoading = false;
  @observable instructors = [];
  @observable instructorsLoading = false;

  @observable orgSettings = null;

  // We'll store the interval ID to clear it on logout or unmount
  refreshIntervalId = null;

  constructor() {
    makeObservable(this);

    // Default axios for normal calls
    this.api = axios.create({ baseURL: config.llmApiBaseUrl });

    // Intercept 401 globally
    this.api.interceptors.response.use(
      response => response,
      error => {
        if (error.response && error.response.status === 401) {
          this.handleLogout();
        }
        return Promise.reject(error);
      }
    );

    // Initial check for any stored tokens
    this.init();
  }

  init = async () => {
    try {
      const token = localStorage.getItem('token');
      const userInfo = localStorage.getItem('userInfo');
      if (token && userInfo) {
        this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        this.profile = JSON.parse(userInfo);
        this.isLoggedIn = true;

        // Start refresh timer if we have a valid session
        this.startRefreshTimer();

        // Load any initial data (e.g. organization details)
        await this.loadInitialData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  @action
  async loadOrgSettings(organizationName) {
    try {
      const response = await ApiStore.getOrgSettings(organizationName);
      runInAction(() => {
        // Set default empty objects for important sections if they're missing
        const safeData = response.data || {};
        this.orgSettings = {
          modulos: safeData.modulos || {},
          configVideos: safeData.configVideos || {},
          configPractica: safeData.configPractica || {},
          // Include any other data
          ...safeData,
        };
      });
    } catch (error) {
      console.error('Error loading org settings:', error);
      // Set safe default in case of error
      runInAction(() => {
        this.orgSettings = {
          modulos: {},
          configVideos: {},
          configPractica: {},
        };
      });
    }
  }

  @action
  async loadInitialData() {
    if (this.isLoggedIn) {
      await this.fetchOrganizationDetails();

      const orgName = this.profile.organization || 'RicoCorp';
      await this.loadOrgSettings(orgName);
    }
  }

  @action
  async fetchOrganizationDetails() {
    try {
      const orgDetails = await ApiStore.getOrganization();
      runInAction(() => {
        // Process and handle potentially null fields
        this.organizationDetails = {
          // Provide fallback values for required fields
          OrganizationName: orgDetails?.OrganizationName || 'RicoCorp',
          // Handle null OrganizationLogo safely
          OrganizationLogo: orgDetails?.OrganizationLogo || {
            type: 'icon',
            icon: CarSimple,
          },
          Title1: orgDetails?.Title1 || 'Virtus',
          Title2: orgDetails?.Title2 || 'Auto',
          Description: orgDetails?.Description || 'Conduciendo el futuro...',
          // Provide fallback values for style properties
          BackgroundColor: orgDetails?.BackgroundColor || '#FFFFFF',
          TextColor: orgDetails?.TextColor || '#000000',
          Font: orgDetails?.Font || 'Arial, sans-serif',
        };
      });
    } catch (error) {
      console.error('Error fetching organization details:', error);
      runInAction(() => {
        // fallback
        this.organizationDetails = {
          OrganizationName: 'RicoCorp',
          OrganizationLogo: { type: 'icon', icon: CarSimple },
          Title1: 'Virtus',
          Title2: 'Auto',
          Description: 'Conduciendo el futuro...',
          BackgroundColor: '#FFFFFF',
          TextColor: '#000000',
          Font: 'Arial, sans-serif',
        };
      });
    }
  }

  // -------------------------------
  // Login / Logout
  // -------------------------------
  @action
  async login(username, password, realm = 'auto') {
    this.loginLoading = true;

    // Clear any existing subscription status flags from session storage
    sessionStorage.removeItem('pendingSetupUser');
    sessionStorage.removeItem('pendingSubscription');
    sessionStorage.removeItem('canceledSubscription');

    try {
      // Use our "this.api" instance to call /api/v1/auth/login
      const response = await this.api.post('/api/v1/auth/login', {
        username,
        password,
        realm,
      });
      const {
        access_token,
        refresh_token,
        user_info,
        warning,
        requires_action,
        subscription_status,
        message,
        organization_code,
      } = response.data;

      // Store the tokens and user info regardless of subscription status
      localStorage.setItem('refreshToken', refresh_token);
      this.setToken(access_token);
      this.setProfile(user_info);

      // Start refreshing the token periodically
      this.startRefreshTimer();

      // Check if subscription setup is required (pending subscription from the login response)
      if (warning === 'subscription_setup_required' && requires_action) {
        console.log('Login response indicates subscription setup required');

        // Store subscription setup required flag and organization code
        runInAction(() => {
          this.isLoggedIn = true;
          this.loginLoading = false;
          this.requiresSubscriptionSetup = true;
        });

        // Store organization code from login response in sessionStorage for the subscription page
        if (organization_code) {
          sessionStorage.setItem('pendingOrgCode', organization_code);
          console.log(
            'Stored organization code from login response:',
            organization_code
          );
        }

        // Store pending setup info in session storage
        sessionStorage.setItem(
          'pendingSetupUser',
          JSON.stringify({
            subscriptionStatus: 'incomplete_setup',
            userId: user_info.user_id,
          })
        );

        // IMPORTANT: Skip additional subscription status check for newly registered users
        // This prevents the error where backend tries to fetch a non-existent Stripe subscription

        // Return subscription setup required information to login handler
        return {
          subscriptionSetupRequired: true,
          userId: user_info.user_id,
          organization_code: organization_code,
          message:
            message ||
            'Es necesario completar la configuración de la suscripción',
        };
      }

      // Normal login success flow
      runInAction(() => {
        this.isLoggedIn = true;
        this.loginLoading = false;
        this.requiresSubscriptionSetup = false;
      });

      await this.loadInitialData();

      // Only check subscription status for users who don't have a pending setup
      // from the login response - this avoids the error with non-existent Stripe subscriptions
      try {
        // For users without requires_action in the login response,
        // we still need to check their subscription status
        const subscriptionStatus =
          await SubscriptionService.getSubscriptionStatus();

        if (subscriptionStatus && subscriptionStatus.subscription_type) {
          console.log(
            'Subscription status check result:',
            subscriptionStatus.subscription_type
          );

          // If subscription is canceled, pre-fetch organization code
          if (subscriptionStatus.subscription_type === 'canceled') {
            try {
              const orgData =
                await SubscriptionService.getCurrentUserOrganizationCode();
              if (orgData && orgData.organization_code) {
                // Store the organization code for subscription renewal
                sessionStorage.setItem(
                  'pendingOrgCode',
                  orgData.organization_code
                );

                // Store org info, using fallbacks for potential null values
                sessionStorage.setItem(
                  'pendingOrgInfo',
                  JSON.stringify({
                    name:
                      orgData.organization_name ||
                      this.profile.organization ||
                      'Tu organización',
                    amount: orgData.subscription_amount || 0,
                  })
                );

                console.log(
                  'Successfully stored organization code:',
                  orgData.organization_code
                );
              }
            } catch (orgError) {
              console.warn('Error pre-fetching organization code:', orgError);
            }
          }

          // Return subscription type for login handler to handle redirects
          return {
            subscriptionType: subscriptionStatus.subscription_type,
            hasSubscription: subscriptionStatus.has_subscription,
          };
        }
      } catch (error) {
        console.warn('Error checking subscription status:', error);
        // Continue with normal login flow if subscription check fails
      }
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      this.loginLoading = false;

      // Handle different error cases
      if (error.response) {
        const errorData = error.response.data;

        // Account manually disabled (can come as 401 during login or 403 after)
        if (errorData.error === 'account_disabled') {
          return { disabled: true, reason: 'manual' };
        }
        // Account disabled due to inactivity
        else if (errorData.error === 'account_disabled_inactivity') {
          return { disabled: true, reason: 'inactivity' };
        }
        // Handle specific error messages for other cases
        else if (errorData.error === 'missing_credentials') {
          return {
            errorMessage:
              'El nombre de usuario y la contraseña son obligatorios',
          };
        } else if (errorData.error === 'invalid_credentials') {
          return { errorMessage: 'Nombre de usuario o contraseña incorrectos' };
        }
      }

      return false;
    }
  }

  @action
  setToken(token) {
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('token', token);

    // Also set the same header on axiosAuthAPI, if you want authorized calls there
    axiosAuthAPI.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  @action
  setProfile(userInfo) {
    runInAction(() => {
      this.profile = {
        _id: userInfo.user_id,
        username: userInfo.name,
        email: userInfo.email,
        organization: userInfo.groups[0]?.replace('/', '') || '',
      };
    });
    localStorage.setItem('userInfo', JSON.stringify(this.profile));
  }

  @action
  handleLogout = () => {
    this.stopRefreshTimer(); // clear the interval
    this.isLoggedIn = false;
    this.profile = {};
    this.requiresSubscriptionSetup = false;
    this.api.defaults.headers.common['Authorization'] = '';
    axiosAuthAPI.defaults.headers.common['Authorization'] = '';

    // Clear all local storage items
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('refreshToken');

    // Clear all session storage items related to subscriptions and login status
    sessionStorage.removeItem('pendingSetupUser');
    sessionStorage.removeItem('pendingSubscription');
    sessionStorage.removeItem('pendingOrgCode');
    sessionStorage.removeItem('pendingOrgInfo');
    sessionStorage.removeItem('canceledSubscription');
    sessionStorage.removeItem('floatingVideoShown');

    // Redirect to login page
    this.redirect = '/login';
  };

  /**
   * Refreshes the current user's subscription status
   * This is used after subscription creation/update to ensure app state reflects current status
   * @returns {Promise<object>} Updated subscription status
   */
  @action
  async refreshSubscriptionStatus() {
    try {
      console.log('Refreshing subscription status...');

      // Clear any subscription-related flags that might be stale
      this.requiresSubscriptionSetup = false;
      sessionStorage.removeItem('pendingSetupUser');
      sessionStorage.removeItem('pendingSubscription');
      sessionStorage.removeItem('canceledSubscription');

      // Load fresh subscription status from the API
      const subscriptionStatus =
        await SubscriptionService.getSubscriptionStatus();
      console.log('Updated subscription status:', subscriptionStatus);

      // Update local state based on the new subscription status
      if (subscriptionStatus) {
        if (
          subscriptionStatus.subscription_type === 'paid' ||
          subscriptionStatus.subscription_type === 'free_user'
        ) {
          // User has a valid subscription - ensure they have full access
          this.requiresSubscriptionSetup = false;
          this.redirect = '/'; // Redirect to dashboard
          return {
            success: true,
            subscriptionType: subscriptionStatus.subscription_type,
            hasSubscription: subscriptionStatus.has_subscription,
          };
        } else if (
          subscriptionStatus.subscription_type === 'needs_setup' ||
          subscriptionStatus.status === 'incomplete_setup'
        ) {
          // User still needs to complete setup
          this.requiresSubscriptionSetup = true;
          sessionStorage.setItem(
            'pendingSetupUser',
            JSON.stringify({
              subscriptionStatus: 'incomplete_setup',
            })
          );
          return {
            success: false,
            subscriptionType: 'needs_setup',
            message:
              'Es necesario completar la configuración de la suscripción',
          };
        } else if (subscriptionStatus.subscription_type === 'canceled') {
          // User has a canceled subscription
          sessionStorage.setItem('canceledSubscription', 'true');
          return {
            success: false,
            subscriptionType: 'canceled',
            message: 'Suscripción cancelada',
          };
        }
      }

      // Default fallback
      return {
        success: false,
        message: 'No se pudo determinar el estado de la suscripción',
      };
    } catch (error) {
      console.error('Error refreshing subscription status:', error);
      return {
        success: false,
        error: error.message,
        message: 'Error al actualizar el estado de la suscripción',
      };
    }
  }

  // -------------------------------
  // Token Refresh Logic
  // -------------------------------
  startRefreshTimer() {
    // Refresh token every 4 minutes (adjust as needed)
    if (!this.refreshIntervalId) {
      this.refreshIntervalId = setInterval(
        () => {
          this.tryRefreshToken();
        },
        4 * 60 * 1000
      );
    }
  }

  stopRefreshTimer() {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
  }

  @action
  async tryRefreshToken() {
    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) return; // nothing to refresh

    try {
      const { access_token, refresh_token } =
        await ApiStore.refreshToken(storedRefreshToken);
      runInAction(() => {
        // update access token in local store & headers
        this.setToken(access_token);
      });
      // update the stored refresh token
      localStorage.setItem('refreshToken', refresh_token);
    } catch (error) {
      console.error('Token refresh failed, logging out user...', error);
      this.handleLogout();
    }
  }

  // Tools (fuzzy searching)
  @computed
  get tools() {
    const fuzzyTools = FuzzySet([...TOOLS.map(tool => tool.title)]).get(
      this.toolsKeyword,
      0.5
    );
    if (fuzzyTools && fuzzyTools.length) {
      const fuzzySummary = fuzzyTools.map(fuzzyTool => fuzzyTool[1]);
      if (fuzzySummary && fuzzySummary.length) {
        return TOOLS.filter(tool => fuzzySummary.includes(tool.title));
      }
    }
    return TOOLS;
  }

  // For copying text
  copyToClipboard = output => {
    if (Array.isArray(output)) {
      output = output.join('\n');
    }
    if (!navigator.clipboard) {
      const textarea = document.getElementById('copy-textarea');
      this.copyToClipboardText = `${output}`;
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      return;
    }
    navigator.clipboard.writeText(output).then(
      () => console.log('Async: Copying to clipboard was successful!'),
      err => console.error('Async: Could not copy text: ', err)
    );
  };

  // --------------- Chat Methods ---------------
  @action
  async loadChatMessages(afterMessageId = null, append = false) {
    this.chatLoading = true;
    try {
      const orgName =
        this.organizationDetails?.OrganizationName || 'DefaultOrg';

      const messages = await ApiStore.getChatMessages(orgName, afterMessageId);

      runInAction(() => {
        if (append && messages && messages.length) {
          this.chatMessages.push(...messages);
        } else {
          this.chatMessages = messages || [];
        }
        this.chatLoading = false;
      });
    } catch (err) {
      console.error('Error loading chat messages:', err);
      runInAction(() => {
        this.chatLoading = false;
      });
    }
  }

  @action
  async postNewChatMessage(recipient, message, recipientUserId = null) {
    try {
      const orgName =
        this.organizationDetails?.OrganizationName || 'DefaultOrg';

      await ApiStore.postChatMessage(
        orgName,
        recipient,
        message,
        recipientUserId
      );
      // Let the UI call loadChatMessages as needed
    } catch (err) {
      console.error('Error posting chat message:', err);
    }
  }

  @action
  async loadInstructors() {
    this.instructorsLoading = true;
    try {
      const orgName =
        this.organizationDetails?.OrganizationName || 'DefaultOrg';

      const data = await ApiStore.getInstructors(orgName);
      runInAction(() => {
        this.instructors = data || [];
        this.instructorsLoading = false;
      });
    } catch (error) {
      console.error('Error loading instructors:', error);
      runInAction(() => {
        this.instructorsLoading = false;
      });
    }
  }
}

const appStore = new AppStore();
export default appStore;
